<script setup>
import { ref, computed, onMounted, onUnmounted, useAttrs } from 'vue';
import { useMapGetter } from 'dashboard/composables/store.js';
import { getUnixTime } from 'date-fns';
import { findSnoozeTime } from 'dashboard/helper/snoozeHelpers';
import { emitter } from 'shared/helpers/mitt';
import { useBulkActions } from 'dashboard/composables/chatlist/useBulkActions.js';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { isEditableTarget } from 'dashboard/composables/useConversationDeleteUndo';
import wootConstants from 'dashboard/constants/globals';
import {
  CMD_BULK_ACTION_SNOOZE_CONVERSATION,
  CMD_BULK_ACTION_REOPEN_CONVERSATION,
  CMD_BULK_ACTION_RESOLVE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import BulkAgentActions from './BulkAgentActions.vue';
import BulkUpdateActions from './BulkUpdateActions.vue';
import BulkLabelActions from './BulkLabelActions.vue';
import BulkTeamActions from './BulkTeamActions.vue';
import CustomSnoozeModal from 'dashboard/components/CustomSnoozeModal.vue';

const props = defineProps({
  conversations: {
    type: Array,
    default: () => [],
  },
  allConversationsSelected: {
    type: Boolean,
    default: false,
  },
  selectedInboxes: {
    type: Array,
    default: () => [],
  },
  showOpenAction: {
    type: Boolean,
    default: false,
  },
  showResolvedAction: {
    type: Boolean,
    default: false,
  },
  showSnoozedAction: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['selectAllConversations', 'deleteSelected']);

defineOptions({
  inheritAttrs: false,
});

const attrs = useAttrs();
// Excluir conversas segue a mesma permissão da exclusão individual.
const { isAdmin } = useAdmin();
const {
  selectedConversations,
  onAssignAgent,
  onAssignLabels,
  onRemoveLabels,
  onAssignTeamsForBulk: onAssignTeam,
  onUpdateConversations,
} = useBulkActions();

const getConversationById = useMapGetter('getConversationById');

const appliedLabelsForSelection = computed(() => {
  const applied = new Set();
  selectedConversations.value.forEach(id => {
    const conversation = getConversationById.value(id);
    (conversation?.labels || []).forEach(label => applied.add(label));
  });
  return Array.from(applied);
});

const showCustomTimeSnoozeModal = ref(false);

function onCmdSnoozeConversation(snoozeType) {
  if (snoozeType === wootConstants.SNOOZE_OPTIONS.UNTIL_CUSTOM_TIME) {
    showCustomTimeSnoozeModal.value = true;
  } else if (typeof snoozeType === 'number') {
    onUpdateConversations('snoozed', snoozeType);
  } else {
    onUpdateConversations('snoozed', findSnoozeTime(snoozeType) || null);
  }
}

function onCmdReopenConversation() {
  onUpdateConversations('open', null);
}

function onCmdResolveConversation() {
  onUpdateConversations('resolved', null);
}

function customSnoozeTime(customSnoozedTime) {
  showCustomTimeSnoozeModal.value = false;
  if (customSnoozedTime === null || customSnoozedTime instanceof Date) {
    onUpdateConversations(
      'snoozed',
      customSnoozedTime === null ? null : getUnixTime(customSnoozedTime)
    );
  }
}

function hideCustomSnoozeModal() {
  showCustomTimeSnoozeModal.value = false;
}

// Computed property with getter/setter to enable v-model usage
const allSelected = computed({
  get: () => props.allConversationsSelected,
  set: value => {
    emit('selectAllConversations', value);
  },
});

// Com conversas selecionadas, a tecla Delete (ou Backspace, o "delete" do Mac) abre a
// mesma confirmação do botão Excluir. Não age em campos de texto, com modificadores nem
// com outro modal aberto; a permissão é a mesma do botão.
const isDeleteKey = event =>
  ['Delete', 'Backspace'].includes(event.key) &&
  !event.ctrlKey &&
  !event.metaKey &&
  !event.altKey &&
  !event.shiftKey;

const hasOpenModal = () =>
  Boolean(document.querySelector('dialog[open], .modal-mask'));

function onDeleteKeydown(event) {
  if (!isAdmin.value || !props.conversations.length) return;
  if (event.defaultPrevented || event.repeat || !isDeleteKey(event)) return;
  if (isEditableTarget(event.target) || hasOpenModal()) return;

  event.preventDefault();
  emit('deleteSelected');
}

onMounted(() => {
  window.addEventListener('keydown', onDeleteKeydown);
  emitter.on(CMD_BULK_ACTION_SNOOZE_CONVERSATION, onCmdSnoozeConversation);
  emitter.on(CMD_BULK_ACTION_REOPEN_CONVERSATION, onCmdReopenConversation);
  emitter.on(CMD_BULK_ACTION_RESOLVE_CONVERSATION, onCmdResolveConversation);
});

onUnmounted(() => {
  window.removeEventListener('keydown', onDeleteKeydown);
  emitter.off(CMD_BULK_ACTION_SNOOZE_CONVERSATION, onCmdSnoozeConversation);
  emitter.off(CMD_BULK_ACTION_REOPEN_CONVERSATION, onCmdReopenConversation);
  emitter.off(CMD_BULK_ACTION_RESOLVE_CONVERSATION, onCmdResolveConversation);
});
</script>

<template>
  <Transition
    enter-active-class="transition-all duration-200 ease-out origin-bottom"
    enter-from-class="opacity-0 scale-95 translate-y-2"
    enter-to-class="opacity-100 scale-100 translate-y-0"
    leave-active-class="transition-all duration-150 ease-in origin-bottom"
    leave-from-class="opacity-100 scale-100 translate-y-0"
    leave-to-class="opacity-0 scale-95 translate-y-2"
  >
    <div
      v-if="conversations.length > 0"
      v-bind="attrs"
      class="px-2 absolute bottom-20 sm:bottom-4 left-1/2 -translate-x-1/2 z-30 w-full origin-bottom"
    >
      <div
        v-if="allConversationsSelected"
        class="flex items-start gap-2 bg-n-amber-2 outline -outline-offset-1 outline-1 outline-n-amber-5 rounded-xl text-xs leading-5 mb-2 py-1.5 px-2.5 text-n-amber-12"
      >
        <span
          class="i-hugeicons-information-circle size-4 shrink-0 mt-0.5"
          aria-hidden="true"
        />
        {{ $t('BULK_ACTION.ALL_CONVERSATIONS_SELECTED_ALERT') }}
      </div>
      <section
        class="rounded-2xl border border-n-weak bg-n-solid-1 shadow-[0_8px_24px_-8px_rgba(27,40,59,0.25)]"
        :aria-label="$t('BULK_ACTION.ACTIONS_LABEL')"
        data-test-id="bulk-actions-card"
      >
        <header
          class="flex items-center gap-2.5 border-b border-n-weak px-3 py-2"
        >
          <Checkbox
            v-model="allSelected"
            :indeterminate="!allConversationsSelected"
            class="flex-shrink-0"
          />
          <p
            class="m-0 min-w-0 flex-1 truncate text-sm font-semibold text-n-slate-12"
            data-test-id="bulk-actions-count"
          >
            {{
              $t('BULK_ACTION.CONVERSATIONS_SELECTED', {
                conversationCount: conversations.length,
              })
            }}
          </p>
          <NextButton
            v-tooltip.top="$t('BULK_ACTION.CLEAR_SELECTION_TOOLTIP')"
            :label="$t('BULK_ACTION.CLEAR_SELECTION')"
            icon="i-hugeicons-cancel-01"
            slate
            ghost
            xs
            class="shrink-0"
            data-test-id="bulk-actions-clear"
            @click="allSelected = false"
          />
        </header>
        <div
          class="flex items-center gap-1 px-2 py-1.5"
          role="toolbar"
          :aria-label="$t('BULK_ACTION.ACTIONS_LABEL')"
        >
          <BulkLabelActions @assign="onAssignLabels" />
          <BulkLabelActions
            action="remove"
            :applied-labels="appliedLabelsForSelection"
            @remove="onRemoveLabels"
          />
          <span class="mx-0.5 h-4 w-px shrink-0 bg-n-weak" aria-hidden="true" />
          <BulkUpdateActions
            :show-resolve="!showResolvedAction"
            :show-reopen="!showOpenAction"
            :show-snooze="!showSnoozedAction"
            @update="onUpdateConversations"
          />
          <span class="mx-0.5 h-4 w-px shrink-0 bg-n-weak" aria-hidden="true" />
          <BulkAgentActions
            :selected-inboxes="selectedInboxes"
            :conversation-count="conversations.length"
            @select="onAssignAgent"
          />
          <BulkTeamActions
            :conversation-count="conversations.length"
            @select="onAssignTeam"
          />
          <NextButton
            v-if="isAdmin"
            v-tooltip.top="$t('BULK_ACTION.DELETE.TOOLTIP')"
            :label="$t('BULK_ACTION.DELETE.BUTTON')"
            icon="i-hugeicons-delete-02"
            ruby
            faded
            xs
            class="ltr:ml-auto rtl:mr-auto shrink-0"
            data-test-id="bulk-actions-delete"
            @click="emit('deleteSelected')"
          />
        </div>
      </section>
    </div>
  </Transition>
  <woot-modal
    v-model:show="showCustomTimeSnoozeModal"
    :on-close="hideCustomSnoozeModal"
  >
    <CustomSnoozeModal
      @close="hideCustomSnoozeModal"
      @choose-time="customSnoozeTime"
    />
  </woot-modal>
</template>
